import React from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { UkDateInput } from "./UkDateInput";

export const errMsg = (e) => formatApiError(e?.response?.data?.detail) || e.message;

const fetchBlob = async (url) => {
  const { data } = await api.get(url, { responseType: "blob" });
  return data;
};

export const openPdf = async (url) => {
  try {
    const blob = await fetchBlob(url);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) { toast.error("Could not open document", { description: errMsg(e) }); }
};

export const downloadFile = async (url, filename) => {
  try {
    const blob = await fetchBlob(url);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  } catch (e) { toast.error("Download failed", { description: errMsg(e) }); }
};

export const printPdf = async (url) => {
  try {
    const blob = await fetchBlob(url);
    const frame = document.createElement("iframe");
    frame.style.display = "none";
    frame.src = URL.createObjectURL(blob);
    frame.onload = () => frame.contentWindow.print();
    document.body.appendChild(frame);
  } catch (e) { toast.error("Print failed", { description: errMsg(e) }); }
};

const STATUS_STYLE = {
  not_created: "bg-slate-200 text-slate-700", draft: "bg-slate-100 text-slate-700 border border-slate-300",
  internal_review: "bg-sky-100 text-sky-800", awaiting_client_signature: "bg-amber-100 text-amber-800",
  partially_signed: "bg-amber-200 text-amber-900", signed_pending_effective: "bg-emerald-50 text-emerald-800 border border-emerald-300",
  active: "bg-emerald-700 text-white", review_due: "bg-orange-200 text-orange-900", expired: "bg-red-100 text-red-800",
  terminated: "bg-red-700 text-white", superseded: "bg-slate-300 text-slate-600 line-through",
  published: "bg-emerald-700 text-white", approved: "bg-emerald-100 text-emerald-800",
};

export const StatusBadge = ({ status, label, testId }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || "bg-slate-100"}`}
    data-testid={testId}>{label || status?.replace(/_/g, " ")}</span>
);

export const Field = ({ label, value, onChange, required, type = "text", multiline, disabled, testId, hint }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}{required && <span className="text-red-600"> *</span>}</Label>
    {type === "date" ? <UkDateInput value={value} onChange={onChange} disabled={disabled} testId={testId} />
      : multiline
      ? <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="rounded-none text-sm min-h-[70px]" data-testid={testId} />
      : <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value || (type === "date" ? null : ""))} disabled={disabled} className="rounded-none" data-testid={testId} />}
    {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

export const PageHead = ({ kicker, title, sub, children }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap">
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{kicker}</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">{title}</h1>
      {sub && <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{sub}</p>}
    </div>
    <div className="flex gap-2 flex-wrap">{children}</div>
  </div>
);

export const OLIVE = { backgroundColor: "var(--hi-olive)" };
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-GB") : "—");
